import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Send, RotateCcw, MessageCircle } from 'lucide-react';
import { ChatMessage, CHARLIE_AVATAR } from './ChatMessage';
import { ChatLeadCard } from './ChatLeadCard';
import { ChatCtaRow, ChatWhereToBuy } from './ChatActions';
import { ChatPartActions } from './ChatPartActions';
import { getChatSessionId, resetChatSession, fetchChatHistory, streamChatMessage } from '../../lib/charlieChat';
import { trackEvent } from '../../lib/analytics';
import { COOKIE_SETTINGS_EVENT, COOKIE_CONSENT_CHANGED_EVENT } from '../CookieBanner';

const CONSENT_KEY = 'ctrl-aero-cookie-consent';
const SUGGESTIONS = [
  'What does the UL101 detect?',
  'Compare the FOTRIC H6 and H7',
  'Where can I buy ctrl.aero products?',
  'How long is the warranty?',
];
const WELCOME = {
  role: 'assistant',
  content: "Hi, I'm **Charlie Echo** — ctrl.aero's resident bat and site guide. Ask me about the UL101, FOTRIC acoustic and thermal imagers, Dalcomm headsets, case studies, Boeing AMM tasks, warranty — or where to buy, and I'll point you to the right people.",
  welcome: true,
};

const hasConsentChoice = () => {
  try { return Boolean(localStorage.getItem(CONSENT_KEY)); } catch (e) { return true; }
};

const CharlieEchoChat = ({ onRequestDemo, onRequestQuote }) => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => getChatSessionId());
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [consentDone, setConsentDone] = useState(hasConsentChoice);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const openedRef = useRef(false);

  useEffect(() => {
    const onChanged = () => setConsentDone(hasConsentChoice());
    const onSettings = () => setConsentDone(false);
    window.addEventListener(COOKIE_CONSENT_CHANGED_EVENT, onChanged);
    window.addEventListener(COOKIE_SETTINGS_EVENT, onSettings);
    return () => {
      window.removeEventListener(COOKIE_CONSENT_CHANGED_EVENT, onChanged);
      window.removeEventListener(COOKIE_SETTINGS_EVENT, onSettings);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchChatHistory(sessionId).then((hist) => {
      if (!cancelled && hist.length) setMessages([WELCOME, ...hist]);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [sessionId]);

  useEffect(() => {
    if (open && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
      if (!openedRef.current) { openedRef.current = true; trackEvent('chat_open'); }
    }
  }, [open]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const send = useCallback(async (text) => {
    const message = text.trim();
    if (!message || busy) return;
    setInput('');
    setBusy(true);
    trackEvent('chat_message');
    const pagePath = location.pathname;
    setMessages((prev) => [...prev, { id: `u-${Date.now()}`, role: 'user', content: message }, { id: `a-${Date.now()}`, role: 'assistant', content: '', pending: true }]);
    const patchLast = (fn) => setMessages((prev) => {
      const next = prev.slice();
      next[next.length - 1] = fn(next[next.length - 1]);
      return next;
    });
    const controller = new AbortController();
    abortRef.current = controller;
    await streamChatMessage({
      sessionId, message, pagePath, signal: controller.signal,
      onDelta: (t) => patchLast((m) => ({ ...m, content: m.content + t })),
      onDone: ({ sources, offer_contact, offer_cta, where_to_buy, parts }) => patchLast((m) => ({ ...m, pending: false, sources, offer_contact, offer_cta, where_to_buy, parts, question: message })),
      onError: (msg) => patchLast((m) => ({ ...m, pending: false, error: true, content: msg })),
    });
    setBusy(false);
  }, [busy, location.pathname, sessionId]);

  const reset = () => {
    abortRef.current?.abort();
    setBusy(false);
    setMessages([WELCOME]);
    setSessionId(resetChatSession());
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); }
  };

  if (location.pathname.startsWith('/admin')) return null;

  return (
    <div data-kb-ignore className="charlie-echo-root">
      {!open && consentDone && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed z-50 bottom-4 right-4 sm:bottom-6 sm:right-6 group flex items-center gap-2 rounded-full bg-[#1e2d3b] pl-1.5 pr-4 py-1.5 text-white shadow-2xl ring-1 ring-white/10 hover:ring-orange-400/60 hover:-translate-y-0.5 transition-[transform,box-shadow,ring-color] duration-300 animate-fade-in"
          aria-label="Ask Charlie Echo"
          data-testid="charlie-launcher"
        >
          <span className="relative">
            <img src={CHARLIE_AVATAR} alt="" className="h-11 w-11 rounded-full object-cover ring-2 ring-orange-400/80 bg-blue-950" />
            <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-emerald-400 ring-2 ring-[#1e2d3b]" />
          </span>
          <span className="text-left leading-tight">
            <span className="block text-sm font-semibold">Ask Charlie Echo</span>
            <span className="block text-[11px] text-blue-200/90">Your ctrl.aero site guide</span>
          </span>
          <MessageCircle className="h-4 w-4 text-orange-300 group-hover:rotate-12 transition-transform" />
        </button>
      )}

      {open && (
        <div
          className="fixed z-50 inset-0 sm:inset-auto sm:bottom-6 sm:right-6 flex flex-col bg-slate-50 sm:rounded-3xl shadow-2xl ring-1 ring-black/10 overflow-hidden sm:w-[400px] md:w-[440px] sm:h-[min(680px,calc(100vh-4rem))] animate-fade-in"
          role="dialog"
          aria-modal="true"
          aria-label="Charlie Echo chat"
          data-testid="charlie-panel"
        >
          <div className="relative flex items-center gap-3 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 pl-2 pr-3 pt-3 pb-2 text-white">
            <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'linear-gradient(#7dd3fc 1px, transparent 1px), linear-gradient(90deg, #7dd3fc 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
            <div className="relative h-[4.75rem] w-44 shrink-0 -my-1" data-testid="charlie-header-avatar">
              <span className="absolute bottom-0 left-1/2 h-14 w-14 -translate-x-1/2 rounded-full bg-blue-950 ring-2 ring-orange-400/80 shadow-inner" />
              <img src="/charlie-echo-bust.png" alt="Charlie Echo the bat" className="absolute bottom-0 left-0 w-44 drop-shadow-[0_6px_10px_rgba(0,0,0,0.45)]" />
            </div>
            <div className="relative min-w-0 flex-1">
              <p className="text-lg font-bold leading-tight">Ask Charlie Echo</p>
            </div>
            <button type="button" onClick={reset} className="relative rounded-full p-2 text-blue-200 hover:bg-white/10 hover:text-white transition-colors" aria-label="Start a new chat" title="New chat" data-testid="charlie-reset">
              <RotateCcw className="h-4 w-4" />
            </button>
            <button type="button" onClick={() => setOpen(false)} className="relative rounded-full p-2 text-blue-200 hover:bg-white/10 hover:text-white transition-colors" aria-label="Close chat" data-testid="charlie-close">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="charlie-messages">
            {messages.map((m, i) => (
              <React.Fragment key={m.id || `${m.role}-${m.created_at || i}`}>
                <ChatMessage message={m} onNavigate={() => setOpen(false)} />
                {m.role === 'assistant' && !m.pending && m.parts?.length > 0 && <ChatPartActions parts={m.parts} onNavigate={() => setOpen(false)} />}
                {m.role === 'assistant' && !m.pending && m.where_to_buy && <ChatWhereToBuy onRequestQuote={onRequestQuote} />}
                {m.role === 'assistant' && !m.pending && m.offer_cta && !m.where_to_buy && <ChatCtaRow onRequestDemo={onRequestDemo} onRequestQuote={onRequestQuote} />}
                {m.role === 'assistant' && m.offer_contact && !m.pending && (
                  <ChatLeadCard sessionId={sessionId} question={m.question} pagePath={location.pathname} onRequestDemo={onRequestDemo} onRequestQuote={onRequestQuote} />
                )}
              </React.Fragment>
            ))}
            {messages.length === 1 && (
              <div className="ml-10 flex flex-wrap gap-2" data-testid="charlie-suggestions">
                {SUGGESTIONS.map((s) => (
                  <button key={s} type="button" onClick={() => send(s)} className="rounded-full border border-blue-200 bg-white px-3 py-1.5 text-xs font-medium text-blue-700 hover:border-orange-300 hover:text-orange-600 transition-colors" data-testid="charlie-suggestion">
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="border-t border-slate-200 bg-white px-3 py-3">
            <div className="flex items-end gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100 transition-shadow">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                rows={1}
                maxLength={1200}
                placeholder="Ask about a product, spec, case study or support…"
                className="max-h-28 min-h-[24px] flex-1 resize-none bg-transparent text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none"
                data-testid="charlie-input"
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-orange-500 text-white shadow hover:bg-orange-600 disabled:opacity-40 disabled:hover:bg-orange-500 transition-colors"
                aria-label="Send"
                data-testid="charlie-send"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default CharlieEchoChat;
